import { useFormik } from 'formik';
import { pick } from 'lodash';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import {
  CompanyAdviserDetails,
  initialValuesAdviserDetails,
  validationSchemaAdviserDetails,
} from '@/modules/entity/components/common/AdvisorDetails.tsx';
import { useDeleteEntityDocumentMutation } from '@/modules/entity/queries/entity.tsx';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Alert, CustomCheckbox, Dropzone, TextInputField } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { AlertBlueIcon } from 'ui/icons';
import { UpdateEntityRequest } from 'api';

interface Document {
  data: string;
  type: string;
}

export enum AdvisorDocType {
  CompanyAdvisorDocument = 'company_advisor_document',
}
//
export const AdviserDetailsHolder = ({
  moveForward,
  entity_type_prefix,
  allowSkip,
}: OnboardingStepProps) => {
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');

  const { id: entity_id = 0 } = useParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    entity_id ? +entity_id : -1,
    !entity_id || +entity_id === 0 || isNaN(+entity_id),
  );

  const getInitialValues = () => {
    return {
      ...initialValuesAdviserDetails,
      ...dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix],
      adviser: {
        ...initialValuesAdviserDetails.adviser,
        ...dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix]?.adviser,
      },
    };
  };

  const formik = useFormik<CompanyAdviserDetails>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema: validationSchemaAdviserDetails,
    initialValues: getInitialValues(),
    onSubmit: async values => {
      setDirty(false);
      const initInfo = pick<CompanyAdviserDetails, keyof CompanyAdviserDetails>(values, [
        'is_financial_adviser_nominated_by_investor',
        'is_authorized_representative',
        'is_investor_received_advise',
        'role_held_with_investor',
        'name_authorized_representative',
        'adviser',
      ]);
      const entityTypeDetails = dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix];
      const currentInfo = pick<CompanyAdviserDetails, keyof CompanyAdviserDetails>(
        entityTypeDetails,
        [
          'is_financial_adviser_nominated_by_investor',
          'is_authorized_representative',
          'is_investor_received_advise',
          'role_held_with_investor',
          'name_authorized_representative',
          'adviser',
        ],
      );
      const initDoc =
        values?.documents?.filter(item => !item.data?.toLowerCase()?.startsWith('http')) ?? [];

      if (!isEqual(initInfo, currentInfo) || initDoc?.length > 0) {
        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            documents: values.documents?.filter(
              item => !item.data?.toLowerCase()?.startsWith('http'),
            ),
            [entity_type_prefix]: {
              ...dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix],
              ...values,
              documents: null,
              adviser: {
                ...dataHolder?.data?.new_data?.entity_data?.[entity_type_prefix]?.adviser,
                ...values.adviser,
              },
            },
          } as UpdateEntityRequest,
        });
      }
      moveForward && moveForward();
    },
  });

  const [dirty, setDirty] = useState(formik.dirty);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);
  useEffect(() => {
    setDirty(formik.dirty);
  }, [formik.dirty]);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation();

  const onFileAccepted = async (file: File) => {
    setIsFileLoading(true);

    const base64 = await fileToBase64(file, true);

    await formik.setFieldValue('documents', [
      {
        type: AdvisorDocType.CompanyAdvisorDocument,
        data: base64,
      },
    ]);
    formik.setFieldError('documents', '');

    setIsFileLoading(false);
  };

  const onFileClear = useCallback(async () => {
    // const current = formik.values.documents.find(
    //   doc => doc.type === AdvisorDocType.CompanyAdvisorDocument,
    // );
    // if (current?.data?.includes('http')) {
    //   await deleteDocument({
    //     entity_id: +entity_id || 0,
    //     type: AdvisorDocType.CompanyAdvisorDocument,
    //   });
    // }

    await formik.setFieldValue('documents', []);
    formik.setFieldError('documents', '');
    setDocumentUrl('');
  }, [deleteDocument, entity_id, formik]);

  const handleChangeCheckbox = (field: string, value: boolean) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
    formik.setErrors({});
  };

  useEffect(() => {
    if (dataHolder?.data?.new_data?.entity_data?.document_files) {
      const currentDocument = dataHolder?.data?.new_data?.entity_data?.document_files.find(
        ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
      );
      if (currentDocument) {
        setDocumentUrl(
          dataHolder?.data?.new_data?.entity_data?.document_files.find(
            ({ type }) => type === AdvisorDocType.CompanyAdvisorDocument,
          )?.url || '',
        );
        formik.setFieldValue('documents', [
          {
            type: AdvisorDocType.CompanyAdvisorDocument,
            data: currentDocument?.url,
          },
        ] as Document[]);
      }
    }
  }, [dataHolder?.data?.new_data?.entity_data?.document_files]);

  return (
    <OnboardingHolderWrapper
      title="Financial adviser details"
      formik={formik}
      onClickContinue={formik.handleSubmit}
      onClickSkip={
        allowSkip
          ? () => {
              setDirty(false);
              setTimeout(() => {
                moveForward && moveForward();
              });
            }
          : undefined
      }
      isLoading={isLoading || isFileLoading}
    >
      <div className="w-full flex flex-col gap-8">
        <Alert
          variant="neutral-2"
          icon={AlertBlueIcon}
          label="The details below are to be completed by your financial adviser"
        />
        <CustomCheckbox
          label="I am an authorised representative or agent as nominated by the investor(s)"
          checked={formik.values.is_authorized_representative}
          onChange={e => handleChangeCheckbox('is_authorized_representative', e.target.checked)}
          error={formik.errors.is_authorized_representative}
        />
        <div className="w-full flex flex-col gap-4">
          <div className="w-full flex flex-col gap-1">
            <p className="text-[14px] leading-[22px]">
              Upload document JPG, PNG or PDF. Up to 10 mb
            </p>
            <p className="text-[12px] leading-[20px]">
              You must attach a valid authority such as Power of Attorney, guardianship order, grant
              of probate, appointment of bankruptcy etc. that is a certified copy. The document must
              be current and complete, signed by the investor or a court official and permits the
              authorised representative or agent to transact on behalf of the investor.
            </p>
          </div>
          <Dropzone
            onFileAccepted={onFileAccepted}
            onFileClear={onFileClear}
            value={documentUrl || formik.values?.documents?.[0]?.data}
            loading={isFileLoading || isDocumentDeletion}
            error={formik.errors.documents as string}
          />
        </div>
        <TextInputField
          label="Full name of authorised representative or agent"
          name="name_authorized_representative"
        />
        <TextInputField
          label="Role held with investor"
          name="role_held_with_investor"
          placeholder="Financial adviser"
          hintLabel="Enter the advisor's professional role or relationship with the investor, for example financial adviser, accountant, or consultant."
        />
        <CustomCheckbox
          label="I am a financial adviser as nominated by the investor"
          checked={formik.values.is_financial_adviser_nominated_by_investor}
          onChange={e =>
            handleChangeCheckbox('is_financial_adviser_nominated_by_investor', e.target.checked)
          }
        />
        <Alert
          variant="neutral-2"
          icon={AlertBlueIcon}
          label="By completing this section of the application form, you are confirming that you are an ASIC registered financial adviser and hold a current Australian Financial Services Licence (AFSL). "
        />
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField
            label="Name of adviser"
            name="adviser.name_of_adviser"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
          <TextInputField
            label="AFSL number"
            name="adviser.afsl_number"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
        </div>
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField
            label="Dealer group"
            name="adviser.dealer_group"
            placeholder="ABC Financial Group Pty Ltd"
            hintLabel="Enter the dealer group or AFSL licensee the financial adviser is associated with."
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
          <TextInputField
            label="Name pf advisory firm"
            name="adviser.name_of_advisory_firm"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
        </div>
        <TextInputField
          label="Postal address"
          name="adviser.postal_address"
          disabled={!formik.values.is_financial_adviser_nominated_by_investor}
        />
        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
          <TextInputField
            label="Suburb"
            name="adviser.suburb"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
          <TextInputField
            label="postcode"
            name="adviser.postcode"
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
        </div>
        <TextInputField
          label="Email address"
          name="adviser.email_address"
          disabled={!formik.values.is_financial_adviser_nominated_by_investor}
        />
        <div className="flex flex-col w-full">
          <CustomCheckbox
            label="The investor has received personal financial product advice in relation to this investment from a licence financial adviser and the advice is current"
            checked={formik.values.is_investor_received_advise}
            onChange={e => handleChangeCheckbox('is_investor_received_advise', e.target.checked)}
            disabled={!formik.values.is_financial_adviser_nominated_by_investor}
          />
          {formik.errors.is_investor_received_advise && (
            <p className="mt-1 text-sm text-[12px] text-error-red">
              {formik.errors.is_investor_received_advise}
            </p>
          )}
        </div>
      </div>
    </OnboardingHolderWrapper>
  );
};
